import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Router, { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import { isEqual } from 'lodash';
import useDeepCompareEffect from 'use-deep-compare-effect';
import Divider from '@components/Divider';
import Grid from '@components/Grid';
import { Box, Container, Flex } from '@components/layout/Grid';
import MetaTags from '@components/MetaTags';
import SearchBar from '@components/Search';
import VideoGenresList from '@ecosystems/VideoGenresList';
import VideosGenresFilters from '@ecosystems/VideosGenresFilters';
import { GET_VIDEO_SETTINGS } from '@lib/apollo';
import useDebounce from '@lib/hooks/useDebounce';
import VideoSettingsBanner from '@organisms/VideoSettingsBanner';
import VideosGenresTaxonomiesSkeleton from '@skeletons/VideosGenresTaxonomiesSkeleton';
import {
  useInstructorByGenreQuery,
  useTaxonomiesLazyQuery,
} from '@gql/generated';

const TAXONOMY_SLUGS = [
  'durations',
  'needs',
  'styles',
  'instructors',
  'flavors',
  'bodyparts',
];

function sortTaxonomies(taxonomies) {
  return [...(taxonomies || [])]
    .map(_item => {
      const item = { ..._item };
      item.terms = [...item.terms].sort((term1, term2) => {
        let a = term1.name,
          b = term2.name;

        if (item.slug === 'durations') {
          // parseInt() will extract the number at the start of the string
          a = parseInt(a);
          b = parseInt(b);
        }
        return a > b ? 1 : -1;
      });
      return { ...item };
    })
    .sort((a, b) => {
      return TAXONOMY_SLUGS.indexOf(a.slug) - TAXONOMY_SLUGS.indexOf(b.slug);
    });
}

const Wrapper = styled.div<{ channel: string }>`
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  background-color: ${({ theme, channel }) => theme.colors.channels[channel]};
  transition: opacity 300ms ease-in-out;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.neutrals.white};
  margin: 5px;
  text-transform: capitalize;
`;

const VideosCount = styled.p`
  font-size: 13px;
  font-weight: normal;
  color: ${props => props.theme.colors.neutrals.white};
  margin: 5px;
  text-transform: lowercase;
`;

const TeacherAvatar = styled.img`
  height: 40px;
  width: 40px;
  border-radius: 50%;
  margin-right: 10px;
`;

const TeacherTitleWrapper = styled.div`
  margin-bottom: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const TeacherTitle = styled.h2`
  margin: 0;
  font-weight: normal;
  ::first-letter {
    text-transform: capitalize;
  }
  font-size: 13px;
  strong {
    font-weight: 600;
    text-transform: capitalize;
  }
`;

const TeacherChannel = props => (
  <Wrapper onClick={() => props.onClick(props.slug)} channel={props.slug}>
    <Title>{props.channel}</Title>
    <VideosCount>
      {props.count} <Trans id={'videosGenres.sessions'} />
    </VideosCount>
  </Wrapper>
);

const TeacherSectionWrapper = styled.div`
  margin-top: 25px;
  border-top: solid ${props => props.theme.colors.secondary['gray-4']} 1px;
  padding-top: 20px;
`;

const TeacherSection = props => {
  const { loading, data } = useInstructorByGenreQuery({
    variables: {
      languages: null,
      genre: props.genre,
      instructor: props.instructor || '',
    },
  });

  if (!props.instructor || props.genre === 'all' || loading) return null;

  const { instructor, videosCountInGenres } = data.instructorByGenre;

  if (videosCountInGenres?.length < 1) return null;
  const name = instructor.name;

  return (
    <TeacherSectionWrapper>
      <TeacherTitleWrapper>
        <TeacherAvatar src={instructor.assets.squareImage} />
        <TeacherTitle>
          <Trans
            id={'videosGenres.discover'}
            values={{ name: <strong>{name}</strong> }}
          />
        </TeacherTitle>
      </TeacherTitleWrapper>
      <Grid iterableItems={videosCountInGenres.filter(x => x.count)} col={4}>
        {({ channel, count, slug }) => (
          <TeacherChannel
            key={channel}
            channel={channel}
            slug={slug}
            onClick={props.onClick}
            count={count}
          />
        )}
      </Grid>
    </TeacherSectionWrapper>
  );
};

const getSelectedTermsFromQuery = q => {
  const newSelectedTerms = {};
  Object.entries(q).forEach(entry => {
    if (TAXONOMY_SLUGS.includes(entry[0]) && entry[1]) {
      newSelectedTerms[entry[0]] = (entry[1] as string)?.split?.(',');
    }
  });
  return newSelectedTerms;
};

const Qquery = q => {
  if (q) {
    return `?q=${encodeURIComponent(q)}`;
  }
  return '';
};

const taxesToQuery = (prefix, newSelectedTerms) => {
  let newQuery = '';
  Object.keys(newSelectedTerms).forEach((taxonomy, index) => {
    const terms = newSelectedTerms[taxonomy] || [];
    newQuery += `${taxonomy}=${terms.join(',')}`;
    if (index + 1 !== Object.keys(newSelectedTerms).length) {
      newQuery += '&';
    }
  });

  if (newQuery.length === 0) {
    return '';
  }

  return `${prefix}${newQuery}`;
};

const VideoGenres = props => {
  const router = useRouter();
  const query = router.query;
  const [genre, setGenre] = useState(query.genre || '');
  const [queryState, setQuery] = useState(query.q || '');
  const [taxonomies, setTaxonomies] = useState([]);
  const initialSelectedTerms = getSelectedTermsFromQuery(query);
  const [selectedTerms, setSelectedTerms] = useState(initialSelectedTerms);
  const debouncedSearchTerm = useDebounce(queryState, 500);
  const { lang } = query;

  const { loading: loadingVideoSettings, data: videoSettingsData } = useQuery(
    GET_VIDEO_SETTINGS
  );

  const [
    getTaxonomies,
    {
      called,
      loading: loadingTaxonomies,
      data: taxonomiesData,
      refetch: refetchTaxonomies,
    },
  ] = useTaxonomiesLazyQuery();

  useEffect(() => {
    const genre = query.genre as string;
    if (videoSettingsData?.videoSettings) {
      if (!called) {
        getTaxonomies({
          variables: {
            genre: genre === 'all' ? '' : genre,
            languages: videoSettingsData?.videoSettings.contentLanguages,
          },
        });
      } else {
        refetchTaxonomies({
          genre: genre === 'all' ? '' : genre,
          languages: videoSettingsData?.videoSettings.contentLanguages,
        });
      }
    }
  }, [videoSettingsData, query.genre]);

  useEffect(() => {
    if (taxonomiesData?.taxonomies && !loadingTaxonomies) {
      setTaxonomies(sortTaxonomies(taxonomiesData?.taxonomies));
    }
  }, [taxonomiesData?.taxonomies, loadingTaxonomies]);

  useEffect(() => {
    const QueryQ = query.q || '';
    const queryGenre = query.genre || '';
    if (
      debouncedSearchTerm !== QueryQ ||
      genre !== queryGenre ||
      !isEqual(selectedTerms, getSelectedTermsFromQuery(router.query))
    ) {
      const search = Qquery(debouncedSearchTerm);
      const queryString = `${search}${taxesToQuery(
        search ? '&' : '?',
        selectedTerms
      )}`;
      Router.replace(
        `/[lang]/videos/genres/[genre]${queryString}`,
        `/${lang}/videos/genres/${genre}${queryString}`,
        { shallow: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, genre, selectedTerms]);

  useDeepCompareEffect(() => {
    if (query.genre !== genre) {
      setGenre(query.genre);
      setSelectedTerms([]);
    } else if (
      !isEqual(selectedTerms, getSelectedTermsFromQuery(router.query))
    ) {
      setSelectedTerms(getSelectedTermsFromQuery(router.query));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleInputChangeSearchBar = newValue => {
    setQuery(newValue);
  };

  const clearAll = () => {
    setSelectedTerms([]);
  };

  const getInstructorFromTaxonomies = () => {
    let instructorSlug;
    Object.keys(props.pageProps?.selectedTerms || []).forEach(tax => {
      if (tax === 'instructors' && props.pageProps?.selectedTerms[tax])
        instructorSlug = props.pageProps?.selectedTerms[tax][0];
    });
    return instructorSlug;
  };

  return (
    <Container
      flexDirection="column"
      mt={[25, 30, 20]}
      px={[15, 20, 20]}
      mb={[25, 30, 20]}
    >
      <MetaTags title={t`opengraph.videos_genres_title`} />
      {/* Search Bar */}
      <Box mb="20px">
        <SearchBar
          id="search_bar"
          onInputChange={handleInputChangeSearchBar}
          defaultInputValue={queryState}
        />
      </Box>

      <Flex justifyContent="space-between" alignItems="center" mb="20px">
        <VideoSettingsBanner />
      </Flex>

      {/* Taxonomies Menu */}
      {loadingVideoSettings || !called || loadingTaxonomies ? (
        <VideosGenresTaxonomiesSkeleton />
      ) : (
        <VideosGenresFilters
          key={genre}
          taxonomies={taxonomies}
          selectedTerms={selectedTerms}
          onChange={setSelectedTerms}
        />
      )}

      {/* Filtered results metadata */}
      <Box as={Divider} mb={20} />
      <VideoGenresList
        taxonomies={taxonomies}
        query={debouncedSearchTerm}
        genre={genre}
        selectedTerms={selectedTerms}
        clearAll={clearAll}
        renderTeachersSection={() => (
          <TeacherSection
            instructor={getInstructorFromTaxonomies()}
            onClick={setGenre}
            genre={genre}
          />
        )}
      />
    </Container>
  );
};

export const getServerSideProps = ctx => {
  if (
    ctx.query.page &&
    // => when navigating from one page to another next calls 'getServerSideProps' on the new page (transition)
    // WE DONT DO REDIRECTION ON PAGE TRANSITION
    ctx.resolvedUrl === ctx.req.url
  ) {
    const url = new URL(ctx.req.url, process.env.DOMAIN);
    url.searchParams.delete('page');

    return {
      redirect: {
        destination: url.pathname + url.search,
        permanent: false,
      },
    };
  }
  return { props: {} };
};

export default VideoGenres;
